import { UnifiedSearchResultModel, Client as SearchClient } from '@bizzkit/ecommerce-search/client';
import { keepPreviousData, QueryClient, useQuery, UseQueryResult } from '@tanstack/react-query';
import { queryKeys } from '@/api/apiConfig';
import { mapProductItem } from '@/api/product/mapping';
import { ProductItemResponse } from '@/api/product/types';
import { ProblemDetails, SearchSettingsResponse } from '@/generated/data.schemas';

export interface FetchProductBandParams {
	readonly searchApiHost: string;
	readonly searchToken: string;
	readonly searchSegment: string;
	readonly bandsScopeId: string;
	readonly productIds: string[];
}

export const fetchProductBand = async (params: FetchProductBandParams): Promise<UnifiedSearchResultModel> => {
	if (!params.searchToken || params.searchToken.length === 0) return Promise.reject('Missing SearchToken');

	if (!params.productIds || params.productIds.length === 0) {
		return Promise.resolve({});
	}

	try {
		const searchClient = new SearchClient({ baseUrl: params.searchApiHost });
		const result = await searchClient.search.search({
			segmentId: params.searchSegment,
			scopeId: params.bandsScopeId,
			authenticationToken: params.searchToken,
			filters: { SearchableIds: { values: params.productIds } },
		});
		return result.data;
	} catch (error) {
		console.error('Missing result from ecommerce-search', error);
		return Promise.resolve({});
	}
};

export const productBandServerSideFetch = (
	queryClient: QueryClient,
	ids: string[],
	settings: SearchSettingsResponse,
): Promise<void> => {
	const params: FetchProductBandParams = {
		searchApiHost: settings.searchApiHost,
		bandsScopeId: settings.bandsScopeId,
		searchSegment: settings.searchSegment,
		searchToken: settings.searchToken,
		productIds: ids,
	};

	return queryClient.prefetchQuery({
		queryKey: queryKeys.product.band(params).queryKey,
		queryFn: () => fetchProductBand(params),
	});
};

function findExactVariants(
	data: UnifiedSearchResultModel,
	ids: string[],
	settings?: SearchSettingsResponse,
): ProductItemResponse[] {
	if (!data.products) {
		return [];
	}
	const mappedItems = data.products?.map((p) => mapProductItem(p, settings)) || null;

	return ids
		.map((id: string) => {
			const variant = mappedItems?.find((item) => {
				return item.skus?.some((sku) => sku.id === id) || item.id === id;
			});
			if (!variant) {
				return null;
			}
			const sku = variant.skus?.find((x) => x.id === id);
			if (!sku) {
				return variant;
			}
			const selectedItem: ProductItemResponse = {
				id: sku.id as string,
				name: sku.name as string,
				brand: variant.brand,
				image: variant.image,
				url: variant.url,
				itemNo: sku.skuNo,
			};
			return selectedItem;
		})
		.filter((x) => x !== null);
}

export const useProductBandQuery = (
	ids: string[],
	isActive: boolean,
	settings?: SearchSettingsResponse,
): UseQueryResult<ProductItemResponse[] | null | undefined, undefined | ProblemDetails> => {
	const params: FetchProductBandParams = {
		searchApiHost: settings?.searchApiHost ?? '',
		bandsScopeId: settings?.bandsScopeId ?? '',
		searchSegment: settings?.searchSegment ?? '',
		searchToken: settings?.searchToken ?? '',
		productIds: ids,
	};

	return useQuery({
		queryKey: queryKeys.product.band(params).queryKey,
		queryFn: () => fetchProductBand(params),
		enabled: !!settings && isActive, // we don't check for the length of ids here. In case of navigating among products, it can result in showing old data.
		placeholderData: keepPreviousData,
		select: (d) => {
			return d.products?.map((p) => mapProductItem(p, settings)) || null;
		},
		retry: false,
	});
};

export const useFindExactVariantsQuery = (
	ids: string[],
	isActive: boolean,
	settings?: SearchSettingsResponse,
): UseQueryResult<ProductItemResponse[] | null | undefined, undefined | ProblemDetails> => {
	const params: FetchProductBandParams = {
		searchApiHost: settings?.searchApiHost ?? '',
		bandsScopeId: settings?.bandsScopeId ?? '',
		searchSegment: settings?.searchSegment ?? '',
		searchToken: settings?.searchToken ?? '',
		productIds: ids,
	};

	return useQuery({
		queryKey: queryKeys.product.band(params).queryKey,
		queryFn: () => fetchProductBand(params),
		enabled: !!settings && isActive, // we don't check for the length of ids here. In case of navigating among products, it can result in showing old data.
		placeholderData: keepPreviousData,
		select: (d) => findExactVariants(d, ids, settings),
		retry: false,
	});
};
